// CadastreInfoComponent.js
import { useState, useEffect } from "react";
import {
  EditableInputComponent,
  EditableSelectComponent,
  EditableTextareaComponent,
  SectionHeader,
} from "components";
import { Document, Upload } from "assets/icons"; // Replace with appropriate icons
import { toast } from "react-toastify";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { useSession } from "@clerk/clerk-react";
import { FileLabel, PictureLabel } from "shared/types/file";
import ModalUploadFileLog from "shared/components/uploadFileModal/uploadFileModal";
import { UnitDTO } from "shared/types/unit";
import ButtonNew from "components/buttonNew/buttonNew";
import { Device, DeviceType, EnergyLabel } from "shared/types/device";
import { ImagesContainer } from "shared/components/imagesContainer/imagesContainer";
import ReactDatePicker from "react-datepicker";

// Type assertion for React 18 compatibility
const DatePicker = ReactDatePicker as any;

interface DeviceDetailComponentProps {
  deviceInfo: Device;
  handleUnitConformityInfoChangeInParent?: (value: any) => any;
  handleSaveInParent?: () => any;
  handleCloseParent?: () => any;
  unit: UnitDTO;
  parentEditMode?: boolean;
  fetchDevices?: () => any;
}

const DeviceDetailComponent = ({
  deviceInfo,
  handleCloseParent,
  handleSaveInParent,
  unit,
  parentEditMode = true,
  fetchDevices,
}: DeviceDetailComponentProps) => {
  const [editMode, setEditMode] = useState(parentEditMode);
  const [editedDeviceInfo, setEditedDeviceInfo] = useState<Device | null>();
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const { session } = useSession();
  const [fileModalOpen, setFileModalOpen] = useState<boolean>(false);
  const [selectedFileLabel, setSelectedFileLabel] = useState<FileLabel | null>(
    null,
  );

  useEffect(() => {
    console.log("Watching details for: ");
    console.log(deviceInfo);
    setEditedDeviceInfo(
      deviceInfo
        ? deviceInfo
        : {
            deviceType: null,
            energyLabel: null,
            devicePicturesFileLogIds: null,
            width: null,
            height: null,
            depth: null,
            location: null,
            installationDate: null,
            invoiceFileLogId: null,
            invoiceFileLog: null,
            technicalFicheFileLogId: null,
            technicalFicheFileLog: null,
            dbValue: null, // sounds (e.g. 35dB)
            description: null,
            brand: null,
            model: null,
            serialNumber: null,
            unit: unit,
          },
    );
  }, []);

  const handleSave = async () => {
    setHasUnsavedChanges(false);
    try {
      if (handleSaveInParent) {
        handleSaveInParent();
      } else {
        let res = await fetchWithAuth(
          `${API_URL}/devices`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(editedDeviceInfo),
          },
          session,
        );
        if (res.ok) {
          fetchDevices && fetchDevices();
          toast.success("Informatie van toestel succesvol bijgewerkt!");
        } else {
          toast.warning("Er is iets misgegaan!");
        }
      }
    } catch (e) {
      console.error(e);
      toast.error(
        "Er is een fout opgetreden bij het bijwerken van de toestel informatie.",
      );
    }
  };
  const handleUploadClick = (fileLabel: FileLabel) => {
    setSelectedFileLabel(fileLabel);
    setFileModalOpen(true);
  };
  const preventNavigationIfUnsaved = (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
  ) => {
    if (hasUnsavedChanges) {
      e.preventDefault();
      toast.warning(
        "Je moet eerst opslaan voor je naar een andere pagina gaat, anders ben je de wijzigingen kwijt",
      );
    }
  };
  const handleDrop = (fileLabel: FileLabel) => {
    setSelectedFileLabel(fileLabel);
    setFileModalOpen(true);
  };
  const getFileSection = (
    fileLabel: FileLabel,
    fileLogId: number | null,
    fileLog: any,
  ) => {
    return (
      <div className="flex flex-col gap-3 rounded-md border border-neutral-primary/20 p-3">
        <header className="flex items-center gap-2 text-sm font-medium">
          <span
            className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30 ${getFileStatusColor(
              fileLogId,
            )}`}
          />
          <p>{fileLabel} aanwezig?</p>
        </header>
        {fileLogId ? (
          <div className="flex gap-4">
            <a
              href={fileLog.sharePointFileUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => preventNavigationIfUnsaved(e)}
            >
              <span className="flex gap-2">
                Bekijk document <Document className="cursor-pointer" />
              </span>
            </a>
            <ButtonNew
              onClick={() => handleUploadClick(fileLabel)}
              icon={<Upload />}
              variant="primaryMoss"
            />
          </div>
        ) : (
          <div
            className="flex justify-center rounded-md border-2 border-dashed border-neutral-primary/20 p-4"
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => handleDrop(fileLabel)}
          >
            <ButtonNew
              onClick={() => handleUploadClick(fileLabel)}
              icon={<Upload />}
              variant="primaryMoss"
            />
          </div>
        )}
      </div>
    );
  };
  const handleFileUploadSuccess = async (fileLogData: any) => {
    const { fileLog } = fileLogData;
    let updatedField = {};
    switch (selectedFileLabel) {
      case FileLabel.ConformiteitsattestUnit:
        updatedField = {
          unitConformityAttestFileLogId: fileLog.id,
          unitConformityAttestFileLog: fileLog,
        };
        break;
      default:
        return;
    }
    const updatedDeviceInfo = { ...deviceInfo, ...updatedField };

    // Save the updated information to the backend
    try {
      const response = await fetchWithAuth(
        `${API_URL}/devices`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(updatedDeviceInfo),
        },
        session,
      );

      if (response.ok) {
        toast.success("Toestel detail succesvol geüpdatet");
        setHasUnsavedChanges(false); // Reset unsaved changes state
        setEditedDeviceInfo(updatedDeviceInfo); // Update local state
      } else {
        throw new Error("Failed to update device information");
      }
    } catch (error) {
      console.error("Error updating device information:", error);
      toast.error("Failed to update device information");
    }
  };
  const getFileStatusColor = (fileLogId: number | null) => {
    if (fileLogId) {
      return "bg-green-500"; // Green for present
    }
  };
  const updateDeviceInfo = (updatedFields: any) => {
    setHasUnsavedChanges(true);
    setEditedDeviceInfo((prev: any) => ({ ...prev, ...updatedFields }));
  };
  return (
    <section className="flex flex-col gap-3 rounded-md bg-white sm:gap-0">
      <SectionHeader
        title="Toestel detail"
        icon={<Document />}
        editMode={editMode}
        sticky={false}
        onEditClick={() => setEditMode(!editMode)}
        onSaveClick={handleSave}
        showEditToggle={true}
        hasUnsavedChanges={hasUnsavedChanges}
        onCloseClick={() => {
          if (handleCloseParent) handleCloseParent();
        }}
      />
      <div className="grid gap-3 sm:grid-cols-2">
        {/* Foto's */}
        {editedDeviceInfo && (
          <ImagesContainer
            onImageUploadSuccess={(fileLogAndCustomData: any) => {
              console.log("Image upload success");
              console.log(fileLogAndCustomData);
              if (!editedDeviceInfo.id)
                updateDeviceInfo({
                  id: fileLogAndCustomData.customData.savedDeviceInfo.id,
                });
              updateDeviceInfo({
                devicePicturesFileLogIds:
                  fileLogAndCustomData.customData.savedDeviceInfo
                    .devicePicturesFileLogIds,
              });
              fetchDevices && fetchDevices();
            }}
            fetchOnlyPredefinedFileLogIds={true}
            customData={{
              unitDeviceInfo: {
                deviceId: editedDeviceInfo && editedDeviceInfo.id,
              },
            }}
            fileLogIds={editedDeviceInfo?.devicePicturesFileLogIds}
            title="Foto's toestel"
            unitId={unit?.id}
            fileLabel={PictureLabel.DevicePictureUnit as any}
          />
        )}
        {/* Toesteltype */}
        <div className="flex flex-col gap-3 rounded-md border border-neutral-primary/20 p-3">
          <header className="flex items-center gap-2 text-sm font-medium">
            <p>Toesteltype?</p>
          </header>
          <EditableSelectComponent
            label="Maak een keuze"
            value={(editedDeviceInfo && editedDeviceInfo?.deviceType) || ""}
            isLoading={false}
            options={Object.values(DeviceType).map((value) => ({
              label: value,
              value,
            }))}
            editMode={editMode}
            onChange={(value) => updateDeviceInfo({ deviceType: value })}
          />
        </div>
        {/* Merk */}
        <div className={`flex flex-col gap-3 rounded-md border p-3`}>
          <header className="flex items-center gap-2 text-sm font-medium">
            <p>Merk</p>
          </header>
          <EditableInputComponent
            label="Merk"
            value={(editedDeviceInfo?.brand as any) || ""}
            editMode={editMode}
            isLoading={false}
            onChange={(value) => updateDeviceInfo({ brand: value })}
          />
        </div>
        {/* Model */}
        <div className={`flex flex-col gap-3 rounded-md border p-3`}>
          <header className="flex items-center gap-2 text-sm font-medium">
            <p>Model</p>
          </header>
          <EditableInputComponent
            label="Model"
            value={(editedDeviceInfo?.model as any) || ""}
            editMode={editMode}
            isLoading={false}
            onChange={(value) => updateDeviceInfo({ model: value })}
          />
        </div>
        {/* Model */}
        <div className={`flex flex-col gap-3 rounded-md border p-3`}>
          <header className="flex items-center gap-2 text-sm font-medium">
            <p>Serienummer</p>
          </header>
          <EditableInputComponent
            label="Serienummer"
            value={(editedDeviceInfo?.serialNumber as any) || ""}
            editMode={editMode}
            isLoading={false}
            onChange={(value) => updateDeviceInfo({ serialNumber: value })}
          />
        </div>
        {/* Locatie */}
        <div className={`flex flex-col gap-3 rounded-md border p-3`}>
          <header className="flex items-center gap-2 text-sm font-medium">
            <p>Waar staat het toestel?</p>
          </header>
          <EditableInputComponent
            label="Locatie"
            value={(editedDeviceInfo?.location as any) || ""}
            editMode={editMode}
            isLoading={false}
            onChange={(value) => updateDeviceInfo({ location: value })}
          />
        </div>
        <div className={`flex flex-col gap-3 rounded-md border p-3`}>
          <header className="flex items-center gap-2 text-sm font-medium">
            <p>Installatiedatum toestel</p>
          </header>
          <DatePicker
            selected={
              editedDeviceInfo?.installationDate
                ? new Date(editedDeviceInfo?.installationDate)
                : null
            }
            onChange={(date: any) => updateDeviceInfo({ installationDate: date })}
            dateFormat="dd/MM/yyyy"
            className="w-full rounded-sm bg-white p-2.5 text-neutral-primary shadow-form-element placeholder:text-neutral-secondary-mid focus:ring-3 focus:ring-opacity-50"
          />
        </div>
        {/* Toestel factuur */}
        {getFileSection(
          FileLabel.ToestelFactuur,
          deviceInfo?.invoiceFileLogId,
          deviceInfo?.invoiceFileLog,
        )}
        {/* Toestel fiche */}
        {getFileSection(
          FileLabel.ToestelFiche,
          deviceInfo?.technicalFicheFileLogId,
          deviceInfo?.technicalFicheFileLog,
        )}
        {/* Energielabel */}
        <div className="flex flex-col gap-3 rounded-md border border-neutral-primary/20 p-3">
          <header className="flex items-center gap-2 text-sm font-medium">
            <p>Energielabel</p>
          </header>
          <EditableSelectComponent
            label="Maak een keuze"
            value={(editedDeviceInfo && editedDeviceInfo?.energyLabel) || ""}
            isLoading={false}
            options={Object.values(EnergyLabel).map((value) => ({
              label: value,
              value,
            }))}
            editMode={editMode}
            onChange={(value) => updateDeviceInfo({ energyLabel: value })}
          />
        </div>
        {/* Width */}
        <div className={`flex flex-col gap-3 rounded-md border p-3`}>
          <header className="flex items-center gap-2 text-sm font-medium">
            <p>Breedte toestel</p>
          </header>
          <EditableInputComponent
            label="Breedte toestel"
            value={(editedDeviceInfo?.width as any) || ""}
            editMode={editMode}
            isLoading={false}
            onChange={(value) => updateDeviceInfo({ width: value })}
          />
        </div>
        {/* Height */}
        <div className={`flex flex-col gap-3 rounded-md border p-3`}>
          <header className="flex items-center gap-2 text-sm font-medium">
            <p>Hoogte toestel</p>
          </header>
          <EditableInputComponent
            label="Hoogte toestel"
            value={(editedDeviceInfo?.height as any) || ""}
            editMode={editMode}
            isLoading={false}
            onChange={(value) => updateDeviceInfo({ height: value })}
          />
        </div>
        {/* Depth */}
        <div className={`flex flex-col gap-3 rounded-md border p-3`}>
          <header className="flex items-center gap-2 text-sm font-medium">
            <p>Diepte toestel</p>
          </header>
          <EditableInputComponent
            label="Diepte toestel"
            value={(editedDeviceInfo?.depth as any) || ""}
            editMode={editMode}
            isLoading={false}
            onChange={(value) => updateDeviceInfo({ depth: value })}
          />
        </div>
        {/* DB Value */}
        <div className={`flex flex-col gap-3 rounded-md border p-3`}>
          <header className="flex items-center gap-2 text-sm font-medium">
            <p>Decibel / geluid (in dB)</p>
          </header>
          <EditableInputComponent
            label="Conformiteitsscore"
            value={(editedDeviceInfo?.dbValue as any) || ""}
            editMode={editMode}
            isLoading={false}
            onChange={(value) => updateDeviceInfo({ dbValue: value })}
          />
        </div>
        {/* Description */}
        <div className="flex flex-col gap-3 rounded-md border border-neutral-primary/20 p-3">
          <header className="flex items-center gap-2 text-sm font-medium">
            <p>Toestel beschrijving</p>
          </header>
          <EditableTextareaComponent
            label="Toestel beschrijving"
            value={(editedDeviceInfo && editedDeviceInfo?.description) || ""}
            isLoading={false}
            editMode={editMode}
            onChange={(value) => updateDeviceInfo({ description: value })}
          />
        </div>
        {/* Modal for File Upload */}
        <ModalUploadFileLog
          isOpen={fileModalOpen}
          onClose={() => setFileModalOpen(false)}
          onSave={handleFileUploadSuccess}
          defaultFileLabel={FileLabel.ToestelFiche}
          unit={unit}
          customLabels={[selectedFileLabel]}
        />
      </div>
    </section>
  );
};

export default DeviceDetailComponent;
